import { NgModule } from '@angular/core';
import { NavigationEnd, Router, RouterModule, Routes } from '@angular/router';

// Guards
import { MsalGuard, MsalService } from '@azure/msal-angular';
import { OnboardingGuard } from 'src/app/guards/onboarding.guard'
import { RoleGuard } from 'src/app/guards/role.guard';
import { LoginGuard } from 'src/app/guards/login.guard'

// layouts
import { BlankLayoutComponent } from './components/layouts/blankLayout.component';
import { EmbedLayoutComponent } from './components/layouts/embedLayout.component';
import { BasicLayoutComponent } from './components/layouts/basicLayout.component';
import { MapLayoutComponent } from './components/layouts/mapLayout.component';
import { PublicLayoutComponent } from './components/layouts/publicLayout.component';

import { LoginComponent } from './components/login/login.component';
import { UnauthorisedComponent } from './components/unauthorised/unauthorised.component';
import { LogoutComponent } from './components/logout/logout.component';

// views
import { OnBoardingComponent } from './views/on-boarding/on-boarding.component';
import { CustomersComponent } from './views/customers/customers.component';
import { ControlComponent } from './views/control/control.component';
import { UsersComponent } from './views/users/users.component';

import { MessageTemplateComponent } from './views/message-template/message-template.component';
import { DistributionComponent } from './views/distribution/distribution.component';
import { DataAnalysisComponent } from './views/data-analysis/data-analysis.component';
import { WidgetComponent } from './views/dashboard/widgets/widget/widget.component';
import { DashboardListComponent } from './views/dashboard-list/dashboard-list.component';
import { DashboardComponent } from './views/dashboard/dashboard.component';
import { DashboardsComponent } from './views/dashboards/dashboards.component';

import { HomeComponent } from './views/home/home.component';
import { SitesComponent } from './views/sites/sites.component';
import { SiteTypeComponent } from './views/site-type/site-type.component';
import { SiteTypesComponent } from './views/site-types/site-types.component';

import { CustomerListComponent } from './views/customer/customer-list/customer-list.component';
import { CustomerSpecificComponent } from './views/customer/customer-specific/customer-specific.component';
import { ProductInstanceSpecificComponent } from './views/customer/product-instance-specific/product-instance-specific.component';

import { DatabusOverviewComponent } from './views/databus/databus-overview/databus-overview.component';
import { DatabusPurchaseComponent } from './views/databus/databus-purchase/databus-purchase.component';
import { DatabusApproveComponent } from './views/databus/databus-approve/databus-approve.component';
import { DatabusHealthComponent } from './views/databus/databus-health/databus-health.component';

import { MarketplaceComponent } from './views/applications/marketplace.component';
import { CeresTagHomeComponent } from './views/applications/ceres-tag/home/home.component';
import { CeresTagFarmComponent } from './views/applications/ceres-tag/farm/farm.component';
import { CeresTagOnBoardComponent } from './views/applications/ceres-tag/onboard/onboard.component';
import { RoomAirQualityHomeComponent } from './views/applications/room-air-quality/home.component';
import { OrganisationalSettingsComponent } from './views/organisational-settings/organisational-settings.component';
import { FireForesightAdminComponent } from './views/applications/fire-foresight/admin/fire-foresight-admin.component';
import { FireForesightCameraConfigsComponent } from './views/applications/fire-foresight/camera-configs/fire-foresight-camera-configs.component';
import { FireForesightSitesComponent } from './views/applications/fire-foresight/sites/fire-foresight-sites.component';
import { FireForesightLayoutComponent } from './components/layouts/fireForesightLayout.component';
import { FireForesightIncidentComponent } from './views/applications/fire-foresight/incident/fire-foresight-incident.component';
import { EucsHomeComponent } from './views/applications/end-user-control-system/home/home.component';
import { FireForesightCameraComponent } from './views/applications/fire-foresight/camera/fire-foresight-camera.component';
import { FireForesightCamerasComponent } from './views/applications/fire-foresight/cameras/fire-foresight-cameras.component';
import { FireForesightIncidentsComponent } from './views/applications/fire-foresight/incidents/fire-foresight-incidents.component';
import { FireForesightStatisticsComponent } from './views/applications/fire-foresight/statistics/fire-foresight-statistics.component';
import { FireForesightCommandComponent } from './views/applications/fire-foresight/command-centre/fire-foresight-command.component';
import { MapLayersComponent } from './views/map-layers/map-layers.component';
import { TestPageComponent } from './test-page/test-page.component';
import { ProductsComponent } from './views/products/products.component';
import { ApiKeysComponent } from './views/api-keys/api-keys.component';
import { UserService } from 'src/api/services';
import { CommonModule } from '@angular/common';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { filter } from 'rxjs/operators';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { SiteMapComponent } from './views/site-map/site-map.component';
import { PanoramaComponent } from './views/applications/fire-foresight/panorama/panorama.component';
import { FireForesightOnBoardComponent } from './views/applications/fire-foresight/onboard/onboard.component';
import { SiteComponent } from './views/site/site.component';
import { AzureAuthGuard } from './guards/azure-auth.guard';
import { SupportHomeComponent } from './views/support/support-home/support-home.component';
import { SupportSearchComponent } from './views/support/support-search/support-search.component';
import { WidgetTextTemplatesComponent } from './views/widget-text-templates/widget-text-templates.component';
import { SmartIrrigationDashboardComponent } from './views/applications/smart-irrigation/dashboard/dashboard.component';
import { SmartIrrigationOnBoardComponent } from './views/applications/smart-irrigation/onboard/onboard.component';
import { SmartIrrigationSchedulesComponent } from './views/applications/smart-irrigation/schedules/schedules.component';
import { FireForesightIncidentsWatchComponent } from './views/applications/fire-foresight/incidents-watch/fire-foresight-incidents-watch.component';
import { FireForesightBurnListComponent } from './views/applications/fire-foresight/burn-list/fire-foresight-burn-list.component';
import { FireForesightBurnListOverviewComponent } from './views/applications/fire-foresight/burn-list-overview/fire-foresight-burn-list-overview.component';
import { FireForesightBurnCoupsComponent } from './views/applications/fire-foresight/burn-coups/fire-foresight-burn-coups.component';
import { FireForesightBurnListMapComponent } from './views/applications/fire-foresight/burn-list-map/fire-foresight-burn-list-map.component';
import { FireForesightWatchComponent } from './views/applications/fire-foresight/watch/fire-foresight-watch.component';
import { CameraWatchComponent } from './views/camera-watch/camera-watch.component';
import { CameraWatchLayoutComponent } from './components/layouts/cameraWatchLayout.component';
import { GeoAssetsComponent } from './views/geo-assets/geo-assets.component';
//import { SupportAdminComponent } from './views/support/support-admin/support-admin.component';
import { SmartIrrigationRegionsComponent } from './views/applications/smart-irrigation/regions/regions.component';
import { SmartIrrigationRegionComponent } from './views/applications/smart-irrigation/region/region.component';
import { SmartIrrigationParksComponent } from './views/applications/smart-irrigation/parks/parks.component';
import { SmartIrrigationSiteComponent } from './views/applications/smart-irrigation/site/site.component';
import { SmartIrrigationSensorTrendComponent } from './views/applications/smart-irrigation/sensor-trend/sensor-trend.component';
import { SmartIrrigationWeatherTrendComponent } from './views/applications/smart-irrigation/weather-trend/weather-trend.component';
import { SmartIrrigationWaterTrendComponent } from './views/applications/smart-irrigation/water-trend/water-trend.component';
import { SmartIrrigationAdministrationComponent } from './views/applications/smart-irrigation/administration/administration.component';
import { SmartIrrigationSystemHealthComponent } from './views/applications/smart-irrigation/system-health/system-health.component';


export const appRoutes: Routes = [
  {
    path: '',
    component: BlankLayoutComponent,
    children: [
      { path: '', component: LoginComponent, canActivate: [] },
      { path: 'login', component: LoginComponent },
      { path: 'logout', component: LogoutComponent },
      { path: 'unauthorised', component: UnauthorisedComponent },
      { path: 'onboarding', component: OnBoardingComponent, canActivate: [MsalGuard] },
      { path: 'application/ceres-tag/onboard', component: CeresTagOnBoardComponent },
    ]
  },
  {
    path: '',
    component: EmbedLayoutComponent,
    children: [
      { path: 'embed/dashboard/:dashboardId', component: DashboardComponent, data : { public: true, embed:true, } },
      { path: 'embed/dashboard/:dashboardId/:uri', component: DashboardComponent, data : { public: true, embed:true, } },
      { path: 'dashboard/:dashboardId/:widgetId/:uri', component: WidgetComponent, canActivate: [MsalGuard, OnboardingGuard]}
    ]
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      { path: 'public/dashboard/:dashboardId', component: DashboardComponent, data : { public: true, embed:false, } },
      { path: 'public/dashboard/:dashboardId/:uri', component: DashboardComponent, data : { public: true, embed:false, } },
      { path: 'public/dashboard-list/:identifier', component: DashboardListComponent, data : { public: true, embed:false, } },
      { path: 'publication/:identifier', component: DashboardComponent, data : { public: true, embed:false, }},
      { path: 'publication/fire-foresight-watch/:identifier', component: FireForesightWatchComponent, data: { public: true, embed: false } },
      { path: 'publication/camera-watch/:identifier', component: CameraWatchComponent, data: { public: true, embed: false } },
      { path: 'test', component: TestPageComponent }
    ]
  },
  {
    path: '',
    component: MapLayoutComponent,
    children: [
      { path: 'sites', component: SitesComponent, canActivate: [MsalGuard, OnboardingGuard]},
    ]
  },
  {
    path: '',
    component: FireForesightLayoutComponent,
    children: [
      { path: 'fire-foresight/incidents', component: FireForesightIncidentsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/incidents-watch/:statusId', component: FireForesightIncidentsWatchComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/incident/:incidentId', component: FireForesightIncidentComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/cameras', component: FireForesightCamerasComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/command', component: FireForesightCommandComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/camera/:siteId', component: FireForesightCameraComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/landscape/:siteId', component: PanoramaComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/burn-list', component: FireForesightBurnListComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/burn-list/:burnListId', component: FireForesightBurnListComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/burn-list-overview/:burnListRegionId/:burnListTypeId', component: FireForesightBurnListOverviewComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/burn-coups/:burnListRegionId', component: FireForesightBurnCoupsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/burn-list-map', component: FireForesightBurnListMapComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/watch', component: FireForesightWatchComponent, canActivate: [MsalGuard, OnboardingGuard]},
    ]
  },
  {
    path: '',
    component: CameraWatchLayoutComponent,
    children: [
      { path: 'camera-watch', component: CameraWatchComponent, canActivate: [MsalGuard, OnboardingGuard] },
    ]
  },
  {
    path: '',
    component: BasicLayoutComponent,
    children: [
      { path: 'home', component: HomeComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'user-statistics', component: SiteMapComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'data/analysis', component: DataAnalysisComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'user/api-keys', component: ApiKeysComponent, canActivate: [MsalGuard, OnboardingGuard] },
      //{ path: 'test/image', component: TestImageAnalysisComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'dashboard/:dashboardId', component: DashboardComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'dashboard/:dashboardId/:uri', component: DashboardComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'dashboards', component: DashboardsComponent, canActivate: [MsalGuard, OnboardingGuard]},      
      { path: 'dashboard-list/:dashboardListId', component: DashboardListComponent, canActivate: [MsalGuard, OnboardingGuard]},      
      // - DataBus Licenses
      { path: 'databus', component: DatabusOverviewComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'databus/purchase', redirectTo: 'databus/purchase/', pathMatch: 'full' },
      { path: 'databus/purchase/:licenseId', component: DatabusPurchaseComponent, canActivate: [MsalGuard, OnboardingGuard]  },
      { path: 'databus/approve', component: DatabusApproveComponent, canActivate: [MsalGuard, OnboardingGuard]  },
      { path: 'databus/health', component: DatabusHealthComponent, canActivate: [MsalGuard, OnboardingGuard]  },
      // - Users
      { path: 'users', component: UsersComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Control
      { path: 'control', component: ControlComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Applications
      { path: 'marketplace', component: MarketplaceComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'application/ceres-tag/home', component: CeresTagHomeComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'application/ceres-tag/farm/:customerId/:farmId', component: CeresTagFarmComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'application/room-air-quality/home', component: RoomAirQualityHomeComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/admin', component: FireForesightAdminComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/admin/camera-configs', component: FireForesightCameraConfigsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'fire-foresight/admin/sites', component: FireForesightSitesComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'application/fire-foresight/home', component: FireForesightOnBoardComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'eucs/dashboard', component: EucsHomeComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'application/smart-irrigation/home', component: SmartIrrigationOnBoardComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'smart-irrigation/admin', component: SmartIrrigationAdministrationComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'smart-irrigation/system-health', component: SmartIrrigationSystemHealthComponent, canActivate: [MsalGuard, OnboardingGuard, RoleGuard], data: { role: 'irrigation-admin' }},
      { path: 'smart-irrigation/controllers', component: SmartIrrigationDashboardComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'smart-irrigation/schedules', component: SmartIrrigationSchedulesComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'smart-irrigation/regions', component: SmartIrrigationRegionsComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/region/:nodeId', component: SmartIrrigationRegionComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/parks', component: SmartIrrigationParksComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/parks/:nodeId', component: SmartIrrigationParksComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/site/:nodeId', component: SmartIrrigationSiteComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/sensor-trend/:nodeId', component: SmartIrrigationSensorTrendComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/weather-trend/:nodeId', component: SmartIrrigationWeatherTrendComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'smart-irrigation/water-trend/:nodeId', component: SmartIrrigationWaterTrendComponent, canActivate: [MsalGuard, OnboardingGuard] },
      { path: 'fire-foresight/statistics', component: FireForesightStatisticsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Customers
      { path: 'customers', component: CustomerListComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'customers/admin', component: CustomersComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'customers/:customerId', component: CustomerSpecificComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'customers/:customerId/:productId/:productInstanceId', component: ProductInstanceSpecificComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Support
      { path: 'support/home', component: SupportHomeComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'support/search', component: SupportSearchComponent, canActivate: [MsalGuard, OnboardingGuard]},
      //{ path: 'support/admin', component: SupportAdminComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Widget Text Templates (Admin)
      { path: 'admin/widget-text-templates', component: WidgetTextTemplatesComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Map Layer Items
      { path: 'map-layers', component: MapLayersComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Products
      { path: 'products', component: ProductsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Distributions
      { path: 'distribution', component: DistributionComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'message-template', component: MessageTemplateComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Site Types
      { path: 'site/:siteTypeId/:siteUri', component: SiteComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'site-type/:siteTypeId', component: SiteTypeComponent, canActivate: [MsalGuard, OnboardingGuard]},
      { path: 'site-types', component: SiteTypesComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Organisational Settings
      { path: 'settings', component: OrganisationalSettingsComponent, canActivate: [MsalGuard, OnboardingGuard]},
      // - Geo Assets
      {path: 'geo-assets', component: GeoAssetsComponent, canActivate: [MsalGuard, OnboardingGuard]},
    ]
  },
  //{ path: '**', redirectTo: '/home' }
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes), CommonModule],
  exports: [RouterModule]
})
export class AppRoutingModule { 

  constructor(private router: Router, private userService: UserService, private appConfig: ApplicationConfigService){ //, private authService: AzureAuthService) {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (event && event.urlAfterRedirects && !event.urlAfterRedirects.startsWith("/publication/")){
        this.logUserActivity(event.urlAfterRedirects);
      }
    });
  }

  lastUrl: string = null;
  logUserActivity(url: string): void {
    //console.log(url);
    //todo: make sure this works with anonymous pages
    if (this.lastUrl != url && this.appConfig.loggedIn) { //} && this.authService.isLoggedIn){
      this.userService.UserLogActivity(url).subscribe();
      this.lastUrl = url;
    }
  }
}
